import { useState, useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Transition } from "@headlessui/react";

import { getAccountInfoById } from "actions/accounts";
import { getWellKnownAssets, searchAssets } from "actions/assets";
import { sendingTransaction } from "actions/loaders";
import { optInAssetToAccount, optOutAssetFromAccount } from "actions/transactions";

import useNotification from "hooks/useNotification";
import useTheme from "hooks/useTheme";

import { findInObjectArrayByMatchingProperties } from "helpers/functions";

import SearchIcon from "theme/icons/Search";
import SearchLoader from "theme/loaders/header/SearchLoader";
import AssetOptInModal from "theme/modals/dashboard/asa/AssetOptInModal";
import AssetOptOutModal from "theme/modals/dashboard/asa/AssetOptOutModal";

const ASA = (props) => {
	const dispatch = useDispatch();
	const { appLanguage, themeColor } = useTheme();
	const { notify } = useNotification();

	const wellKnownAssets = useSelector((state) => state.WellKnownAssets);
	const liveSearch = useSelector((state) => state.searchAssets);
	const liveSearchLoading = useSelector((state) => state.LiveSearchLoading);
	const algoChain = useSelector((state) => state.AppPreferences.algoChain);
	const accountInfo = useSelector((state) => state.AccountInfo);

	const [activeTab, setActiveTab] = useState(0);

	const [accountAssetsToRemove, setAccountAssetsToRemove] = useState(null);
	const [suggestedAssets, setSuggestedAssets] = useState(null);
	const [liveSearchQuery, setLiveSearchQuery] = useState(false);

	const [assetOptInModalIsOpen, setAssetOptInModalIsOpen] = useState(false);
	const [assetOptInModalInfo, setAssetOptInModalInfo] = useState(null);

	const [assetOptOutModalIsOpen, setAssetOptOutModalIsOpen] = useState(false);
	const [assetOptOutModalInfo, setAssetOptOutModalInfo] = useState(null);

	useEffect(() => {
		dispatch(getWellKnownAssets(algoChain));

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (wellKnownAssets?.length) GenerateSuggestedAssetsList();
		if (accountInfo?.assets) GenerateAccountAssetsList();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wellKnownAssets, accountInfo]);

	useEffect(() => {
		if (liveSearchQuery) {
			dispatch(searchAssets(liveSearchQuery, algoChain));
		}
	}, [dispatch, liveSearchQuery, algoChain]);

	const CheckIfAssetAvailableInAccount = (asset) => {
		const assetInfo = accountInfo?.assets.find((item) => item?.["asset-id"] === asset?.id);

		if (assetInfo?.["asset-id"] && assetInfo?.deleted === false) notify("warning", "It's already in your account");
		else {
			setAssetOptInModalInfo(asset);
			setAssetOptInModalIsOpen(true);
		}
	};

	const OptInAsset = async () => {
		dispatch(sendingTransaction(true));

		await optInAssetToAccount(
			{ "account-address": accountInfo?.address, "asset-id": assetOptInModalInfo?.id },
			algoChain
		).then((data) => {
			if (data?.txID) props.setActiveDashboardTab(0);
		});

		dispatch(sendingTransaction(false));
	};

	const OptOutAsset = async () => {
		dispatch(sendingTransaction(true));

		await optOutAssetFromAccount(
			{
				"account-address": accountInfo?.address,
				"asset-id": assetOptOutModalInfo?.["asset-id"],
				creator: assetOptOutModalInfo?.creator,
			},
			algoChain
		).then((data) => {
			if (data?.txID) {
				setTimeout(() => {
					dispatch(getAccountInfoById(accountInfo?.address, algoChain));
				}, 5000);
			}
		});

		setAssetOptOutModalIsOpen(false);
		dispatch(sendingTransaction(false));
	};

	const GenerateAccountAssetsList = async () => {
		let assetArray = [];

		for (let asset of accountInfo?.assets) {
			if (asset?.amount === 0 && asset?.["is-nft"] === false && asset?.deleted === false) assetArray.push(asset);
		}

		setAccountAssetsToRemove(assetArray);
	};

	const GenerateSuggestedAssetsList = async () => {
		let assetArray = [];

		for (let wellKnownAsset of wellKnownAssets) {
			const asset = findInObjectArrayByMatchingProperties(accountInfo.assets, {
				"asset-id": parseInt(wellKnownAsset?.index),
			});

			if (!asset?.length || asset?.[0]?.deleted === true) assetArray.push(wellKnownAsset);
		}

		setSuggestedAssets(assetArray);
	};

	return (
		<Transition
			as={Fragment}
			appear={true}
			show={true}
			enter="transition-opacity duration-500"
			enterFrom="opacity-0"
			enterTo="opacity-100"
		>
			<div className="flex w-full items-center justify-center">
				<div className="flex flex-col w-full items-center justify-center space-y-7 py-4">
					<div className="flex w-full items-center justify-center space-x-5">
						{Array.from({ length: 2 }, (_, index) => (
							<div
								className={`text-md font-bold cursor-pointer ${themeColor?.text} ${
									activeTab === index && `border-b-2 ${themeColor?.border}`
								}`}
								key={index}
								onClick={() => setActiveTab(index)}
							>
								{index === 0 ? appLanguage?.add : appLanguage?.remove}
							</div>
						))}
					</div>

					{activeTab === 0 ? (
						<div className="flex flex-col w-full space-y-7">
							<div className="flex w-full relative items-center">
								<SearchIcon className="w-5 absolute left-2.5" fill={themeColor?.hex} />
								<input
									type="text"
									className={`w-full block p-3 pl-10 text-sm ${themeColor?.text} ${themeColor?.bg} bg-opacity-10 rounded-lg focus:border-gray-300 focus:outline-none`}
									placeholder={appLanguage?.searchAssets}
									required
									onChange={(e) => setLiveSearchQuery(e.target.value)}
								/>
								{/* <button
									type="submit"
									className={`w-fit text-white absolute right-2.5 ${themeColor?.bg} hover:shadow-lg smooth font-medium rounded-lg text-xs px-4 py-2`}
								>
									Search
								</button> */}
							</div>

							<div className="flex-1 w-full overflow-y-auto">
								{suggestedAssets?.length ? (
									<div>
										<p>{appLanguage?.suggested}</p>
										{suggestedAssets.map((asset, index) => (
											<div
												className={`flex w-full items-center rounded-lg p-2 cursor-pointer hover:shadow-lg hover:border`}
												key={index}
												onClick={() => CheckIfAssetAvailableInAccount(asset)}
											>
												<div className={`flex w-full items-center space-x-3 py-1`}>
													<img
														className={`w-6 h-6 ${themeColor?.text}`}
														alt="asset"
														loading="lazy"
														src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${asset?.index}&width=30`}
													/>
													<div className="flex items-center space-x-1">
														<h1 className={`text-sm ${themeColor?.text} pr-1`}>
															{asset?.params?.name}
														</h1>
														<p className={`text-sm ${themeColor?.text}`}>({asset?.index})</p>
													</div>
												</div>
												<div className="grid grid-cols-4 gap-4">
													<div className="h-2 rounded col-span-3"></div>
												</div>
											</div>
										))}
									</div>
								) : (
									<></>
								)}
								{liveSearchLoading ? (
									<SearchLoader />
								) : liveSearchQuery && liveSearch?.data?.length ? (
									liveSearch.data.map((asset, index) => (
										<div
											className={`flex w-full items-center rounded-lg p-2 cursor-pointer hover:shadow-lg hover:border`}
											key={index}
											onClick={() => CheckIfAssetAvailableInAccount(asset)}
										>
											<div className={`flex w-full items-center space-x-3 py-1`}>
												<img
													className={`w-6 h-6 ${themeColor?.text}`}
													alt="asset"
													loading="lazy"
													src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${asset?.id}&width=30`}
												/>
												<div className="flex items-center space-x-1">
													<h1 className={`text-sm ${themeColor?.text} pr-1`}>{asset?.name}</h1>
													<p className={`text-sm ${themeColor?.text}`}>({asset?.id})</p>
												</div>
											</div>
											<div className="grid grid-cols-4 gap-4">
												<div className="h-2 rounded col-span-3"></div>
											</div>
										</div>
									))
								) : (
									liveSearchQuery && (
										<div className="p-3 hover:bg-white cursor-pointer">
											<div className="flex w-full space-y-6 py-2">
												<div className="h-2 rounded flex items-center">
													<h1 className={`text-md font-semibold ${themeColor?.text} pr-1`}>
														{liveSearchQuery} -
													</h1>
													<p className={`text-sm ${themeColor?.text}`}>{appLanguage?.noAssetsFound}</p>
												</div>
											</div>
											<div className="grid grid-cols-4 gap-4">
												<div className="h-2 rounded col-span-3"></div>
											</div>
										</div>
									)
								)}
							</div>
						</div>
					) : (
						<div className="flex-1 w-full overflow-y-auto">
							{accountAssetsToRemove?.length ? (
								accountAssetsToRemove.map((asset, index) => (
									<div
										className={`flex w-full items-center rounded-lg p-2 cursor-pointer hover:shadow-lg hover:border`}
										key={index}
										onClick={() => {
											setAssetOptOutModalInfo(asset);
											setAssetOptOutModalIsOpen(true);
										}}
									>
										<div className={`flex w-full items-center space-x-3 py-1`}>
											<img
												className={`w-6 h-6 ${themeColor?.text}`}
												alt="asset"
												loading="lazy"
												src={`${process.env.REACT_APP_DAFFI_MAINNET_URL}/assets/image?assetID=${asset?.["asset-id"]}&width=30`}
											/>
											<div className="flex items-center space-x-1">
												<h1 className={`text-sm ${themeColor?.text} pr-1`}>{asset?.name}</h1>
												<p className={`text-sm ${themeColor?.text}`}>({asset?.["asset-id"]})</p>
											</div>
										</div>
										<div className="grid grid-cols-4 gap-4">
											<div className="h-2 rounded col-span-3"></div>
										</div>
									</div>
								))
							) : (
								<p className={`flex w-full items-center justify-center text-sm font-medium ${themeColor?.text}`}>
									{appLanguage?.noAssetsAvailableToRemove}
								</p>
							)}
						</div>
					)}
				</div>

				<AssetOptInModal
					isOpen={assetOptInModalIsOpen}
					setIsOpen={setAssetOptInModalIsOpen}
					modalInfo={assetOptInModalInfo}
					action={() => OptInAsset()}
				/>

				<AssetOptOutModal
					isOpen={assetOptOutModalIsOpen}
					setIsOpen={setAssetOptOutModalIsOpen}
					modalInfo={assetOptOutModalInfo}
					action={() => OptOutAsset()}
				/>
			</div>
		</Transition>
	);
};

export default ASA;
